// src/pages/Login.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getLoginUrl } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useLanguage();
  
  // Store the return path if specified
  useEffect(() => {
    if (location.state && location.state.returnPath) {
      sessionStorage.setItem('returnPath', location.state.returnPath);
    }
    
    // Redirect if already authenticated
    if (isAuthenticated()) {
      const returnPath = sessionStorage.getItem('returnPath') || '/';
      sessionStorage.removeItem('returnPath');
      navigate(returnPath);
    }
  }, [location, navigate]);

  const handleLogin = () => {
    setIsLoading(true);
    // Redirect to Bungie Auth URL
    window.location.href = getLoginUrl();
  };

  return (
    <div className="max-w-md mx-auto bg-gray-800 rounded-lg p-8 shadow-lg">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-destiny-gold mb-4">{t('login.title')}</h1>
        <p className="text-gray-300">
          {t('login.description')}
        </p>
      </div>
      
      <div className="space-y-4">
        <button
          onClick={handleLogin}
          disabled={isLoading}
          className={`w-full py-3 px-4 rounded font-medium flex items-center justify-center ${
            isLoading 
              ? 'bg-gray-600 text-gray-300 cursor-wait' 
              : 'bg-destiny-blue hover:bg-opacity-90 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('login.connecting')}
            </>
          ) : (
            <>{t('login.signInWithBungie')}</>
          )}
        </button>
        
        <div className="bg-gray-700 rounded-lg p-4">
          <h3 className="text-white font-medium mb-2">{t('login.whySignIn')}</h3>
          <ul className="text-gray-300 text-sm space-y-2">
            <li>• {t('login.benefits.trackAchievements')}</li>
            <li>• {t('login.benefits.personalizedGuides')}</li>
            <li>• {t('login.benefits.trackProgress')}</li>
            <li>• {t('login.benefits.shareAchievements')}</li>
          </ul>
        </div>
        
        <div className="bg-gray-700 rounded-lg p-4">
          <h3 className="text-white font-medium mb-2">{t('login.securityNote')}</h3>
          <p className="text-gray-300 text-sm">
            {t('login.securityDescription')}
          </p>
        </div>
        
        <div className="text-xs text-gray-400 text-center">
          {t('login.termsAgreement')}
        </div>
      </div>
    </div>
  );
};

export default Login;